import { useEffect, useId, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * 최소 접근성 모달 프리미티브(코드베이스 최초 도입) — `role="dialog"`·`aria-modal`·Esc/배경 클릭 닫기만
 * 갖춘다. 포커스 트랩 등 고급 기능은 넣지 않는다(설계 D7 — 과설계 회피, 필요해지면 그때 확장).
 *
 * `document.body` 에 포털로 렌더한다 — `Card` 레이아웃 안에 중첩되면 `overflow`/`z-index` 로 잘릴 수 있다.
 */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  const titleId = useId()

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-700/40 px-4 py-8"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => {
          event.stopPropagation()
        }}
        className="max-h-full w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-lg"
      >
        <h2 id={titleId} className="mb-4 text-base font-semibold text-ink-700">
          {title}
        </h2>
        {children}
      </div>
    </div>,
    document.body
  )
}
